import { Component, Suspense, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Box, CircularProgress, useMediaQuery } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InShellContext } from '../../app/pageHeader';
import { OopsPage } from '../../pages/OopsPage';
import '../i18n';
import { SaSidebar } from './SaSidebar';
import { SaTopBar } from './SaTopBar';
import { SaPageHeader } from './SaPageHeader';

/**
 * Error boundary around the console's routed content — swaps in `OopsPage` on a
 * render throw while keeping the console chrome intact (mirrors the customer
 * `AppShell`'s boundary; React error boundaries must be class components).
 */
class ConsoleErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error in Site Admin console', error, info);
  }

  render(): ReactNode {
    return this.state.hasError ? <OopsPage /> : this.props.children;
  }
}

function ConsoleFooter() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap',
        mt: 4.5,
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        fontSize: 12,
        color: 'text.secondary',
      }}
    >
      <span>{t('siteAdmin:shell.footer.copyright')}</span>
      <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {t('siteAdmin:shell.footer.build', { build: import.meta.env.MODE })}
      </Box>
    </Box>
  );
}

/**
 * The DecisionPoint Site Admin console chrome — a distinct shell from the
 * customer portal (`AppShell`): slate/navy rail with the "DecisionPoint · Site
 * administration" wordmark, a global bar carrying the "Internal — all customers"
 * scope pill + cross-customer search + operator account menu, and a per-page
 * header. Rendered only under the `RequireRole="SiteAdministrator"` gate
 * (`routes.tsx`); when the operator impersonates, the app swaps to the
 * customer's branded `AppShell` instead — the two never show at once.
 */
export function SiteAdminShell() {
  const isMobile = useMediaQuery('(max-width:959.95px)');
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <InShellContext value={true}>
      {/* Viewport-height and non-scrolling; only <main> scrolls. Same correction as
          AppShell (BUG-018): `minHeight: 100vh` is a floor, so tall content grew the
          column, the row, and the sidebar with them — the sidebar's footer ended up
          at the foot of the document instead of the screen, and the `overflowY: auto`
          below could never engage because nothing bounded its height. */}
      <Box
        sx={{ height: '100vh', overflow: 'hidden', display: 'flex', bgcolor: 'background.default' }}
      >
        <SaSidebar
          isMobile={isMobile}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {/* `sticky` is now inert — the header sits outside the scrolling area, so
              there is nothing for it to stick within. Kept for the z-index layer it
              establishes, which the menus rendered from the top bar sit above. */}
          <Box sx={{ position: 'sticky', top: 0, zIndex: 20 }}>
            <SaTopBar isMobile={isMobile} onMenuClick={() => setMobileOpen(true)} />
            <SaPageHeader />
          </Box>
          <Box
            component="main"
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              px: { xs: '16px', md: '28px' },
              py: { xs: '18px', md: '24px' },
            }}
          >
            <ConsoleErrorBoundary>
              <Suspense
                fallback={
                  <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                }
              >
                <Outlet />
              </Suspense>
            </ConsoleErrorBoundary>
            <ConsoleFooter />
          </Box>
        </Box>
      </Box>
    </InShellContext>
  );
}
